import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { env } from '@/lib/env'
import { tokenStorage } from '@/lib/token-storage'

/**
 * Single shared API slice. Every feature injects its endpoints here so we get
 * one cache, one middleware, one base query with auth + tenant headers.
 *
 * Auth model: bearer JWT with silent-refresh on 401. The backend exposes
 *   POST /v1/auth/refresh (auth-guarded) which returns a fresh token.
 *   On a 401 from any other endpoint we try refresh exactly once, then retry.
 *   If refresh itself 401s, we hard-logout to /login.
 */

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.apiBaseUrl,
  prepareHeaders: (headers) => {
    const token = tokenStorage.getAccess()
    if (token) headers.set('Authorization', `Bearer ${token}`)

    // super_admin acting within a tenant sends the active company id.
    const companyId = tokenStorage.getCompanyId()
    if (companyId) headers.set(env.tenantHeader, companyId)

    // Tell the backend which app this is, so it can reject platform accounts on
    // the tenant portal (and tenant accounts on the admin console) at login.
    headers.set('X-DistroOps-App', env.appKind)

    headers.set('Accept', 'application/json')
    return headers
  },
})

let refreshPromise: Promise<boolean> | null = null

async function attemptRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  refreshPromise = (async () => {
    try {
      const token = tokenStorage.getAccess()
      if (!token) return false
      const resp = await fetch(`${env.apiBaseUrl}/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })
      if (!resp.ok) return false
      const body = await resp.json()
      const fresh = body?.data?.access_token
      if (typeof fresh === 'string' && fresh.length > 0) {
        tokenStorage.set(fresh)
        return true
      }
      return false
    } catch {
      return false
    } finally {
      // Allow next refresh after this one settles
      setTimeout(() => {
        refreshPromise = null
      }, 0)
    }
  })()
  return refreshPromise
}

const baseQueryWithAuth: typeof rawBaseQuery = async (args, apiCtx, extraOptions) => {
  let result = await rawBaseQuery(args, apiCtx, extraOptions)

  if (result.error && result.error.status === 401) {
    const url = typeof args === 'string' ? args : args.url
    // Don't try to refresh on the login or refresh requests themselves.
    if (!url.includes('/auth/login') && !url.includes('/auth/refresh')) {
      const refreshed = await attemptRefresh()
      if (refreshed) {
        // Retry the original request with the fresh token
        result = await rawBaseQuery(args, apiCtx, extraOptions)
      }
      if (result.error && result.error.status === 401) {
        tokenStorage.clear()
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      }
    }
  }

  return result
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'Auth',
    'User',
    'Role',
    'Permission',
    'Company',
    'Customer',
    'Lead',
    'Product',
    'Order',
    'Invoice',
    'Payment',
    'Visit',
    'CooldownRule',
    'OverrideRequest',
    'Module',
    'Driver',
    'Vehicle',
    'DeliveryRun',
    'Pallet',
    'Shipment',
    'Carrier',
    'LabelTemplate',
    'CodHandover',
    'CodReconciliation',
    'BankImport',
    'EcommerceStore',
    'SyncRun',
    'ZatcaConfig',
    'MarketplaceListing',
    'MarketplaceOrder',
    'MarketplaceRecurring',
    'SampleAllocation',
    'Einvoice',
    'Notification',
    'NotificationFailure',
    'AdminPlan',
    'AdminModule',
    'AdminTenant',
    'AdminDashboard',
    'AdminMarketplace',
    'TenantOverride',
    'PlatformRole',
    'PlatformStaff',
    'AdminIndustryTemplate',
    'DriverPayRule',
    'DriverEarning',
    'DriverShift',
    'DriverCod',
    'PickList',
    'Custody',
  ],
  endpoints: () => ({}),
})
